<template>
  <div>
    <div class="stock-match-banner">
      <div v-if="!!matchedStockCar" class="single-car-container">
        <div class="stock-match-title">
          {{ i18n.t('/stockmatch/stock-advert-title') }}
        </div>
        <matching-stock-car
          :image-url="matchedStockCar.ImageExterior ? matchedStockCar.ImageExterior : undefined"
          :display-name="`${configuration.ModelName} ${matchedStockCar.Trimline}`"
          :price="parseFloat(matchedStockCar.ConsumerPrice)"
          :engine="matchedStockCar.EngineDescription"
          :transmission="matchedStockCar.Transmission"
          :color-code="matchedStockCar.ColorCode + ' ' + matchedStockCar.Color"
          :interior-code="matchedStockCar.InteriorCode"
          :options="getMatchOptions(matchedStockCar)"
          :is-full-match="matchedStockCar.IsFullMatch"
          :matching-items="getMatchingItems()"
          :car-status="i18n.t(`/stockmatch/carstatus/${matchedStockCar.CarStatus}`)"
          :kom-number="
            (bestMatchingStockCars.resultsDealer.includes(matchedStockCar)
              ? i18n.t('/stockmatch/komnumber/dealer')
              : i18n.t('/stockmatch/komnumber/national')) +
            ' ' +
            matchedStockCar.CommissionNumber
          "
          @stockcar-details="setStockCarDetails(matchedStockCar.CommissionNumber)"
          @reservation-status="setReservationStatusId(matchedStockCar.CommissionNumber)"
          @select-car="showMatchingStockCarToNewQuotePrompt(matchedStockCar)"
        />
      </div>
    </div>

    <stock-car-detail-new
      v-if="stockCarDetails && dealerSettings.newStockFlowEnabled"
      :stock-car="stockCarDetails"
      @on-error="setStockCarDetails(null)"
      @on-cancel="setStockCarDetails(null)"
      @close="setStockCarDetails(null)"
    />
    <stock-car-detail
      v-else-if="stockCarDetails && !dealerSettings.newStockFlowEnabled"
      :stock-car-id="stockCarDetails.Id"
      :title="`${stockCarDetails.Configuration.ModelName} ${stockCarDetails.Configuration.TrimlineName}`"
      @on-error="setStockCarDetails(null)"
      @on-cancel="setStockCarDetails(null)"
      @close="setStockCarDetails(null)"
    />

    <matching-stock-car-prompt
      :show="showMatchingStockCarPrompt"
      @close="closeMatchingStockCarToNewQuotePrompt()"
      @confirm="applyToNewQuote"
    />

    <modal
      v-if="stockCarForReservationStatus && dealerSettings.newStockFlowEnabled"
      id="stock-car-reservation-status"
      :title="i18n.t('/StockDashboard/ReservationStatus/Title')"
      @close="setReservationStatusId(null)"
    >
      <stock-reservation-status-new :stock-car="stockCarForReservationStatus" />
    </modal>
    <modal
      v-else-if="stockCarForReservationStatus && !dealerSettings.newStockFlowEnabled"
      id="stock-car-reservation-status"
      :title="i18n.t('/StockDashboard/ReservationStatus/Title')"
      @close="setReservationStatusId(null)"
    >
      <stock-reservation-status :stock-car-id="stockCarForReservationStatus.Id" />
    </modal>
  </div>
</template>

<script>
import Modal from '@/components/generic/presentational/molecules/modal/Modal.vue'
import MatchingStockCar from '@/components/generic/presentational/molecules/matchingStockCar/MatchingStockCar.vue'
import MatchingStockCarPrompt from '@/components/generic/presentational/molecules/matchingStockCarPrompt/MatchingStockCarPrompt.vue'
import { mapMutations, mapState } from 'vuex'
import StockReservationStatusNew from '@/components/generic/presentational/organisms/stockDashboardNew/StockReservationStatusNew.vue'
import StockReservationStatus from '@/components/generic/presentational/molecules/stockReservationStatus/StockReservationStatus.vue'
import StockCarDetail from '@/components/generic/presentational/organisms/stockCarDetail/StockCarDetail.vue'
import StockCarsNewApi from '@/api/server/StockCarsNew'
import stockCarsService from '@/services/stockCarsService'
import { union, sortBy } from 'lodash'
import stockMatchOptionType from '@/helpers/enums/stockMatchOptionType'
import StockMatching from '@/api/server/StockMatching'
import StockCarDetailNew from '@/components/generic/presentational/organisms/stockDashboardNew/stockCarDetail/StockCarDetailNew.vue'

export default {
  components: {
    StockCarDetailNew,
    StockCarDetail,
    Modal,
    MatchingStockCar,
    StockReservationStatusNew,
    StockReservationStatus,
    MatchingStockCarPrompt,
  },
  data() {
    return {
      i18n: window.VUE_CLI_EXPORTS.plugins.i18n,
      filterDrive: true,
      filterColor: false,
      filterInterior: false,
      filterOptions: {},
      stockCarDetails: null,
      stockCarForReservationStatus: null,
      showMatchingStockCarPrompt: false,
      selectedMatchedStockCar: null,
    }
  },
  computed: {
    ...mapState('stockcars', ['bestMatchingStockCars']),
    ...mapState('quote', ['quote']),
    ...mapState('configuration', ['configurationCode', 'configuration', 'price']),
    ...mapState('user', ['dealerSettings']),
    allCars() {
      return (
        this.bestMatchingStockCars.resultsDealer?.concat(
          this.bestMatchingStockCars.resultsCountry
        ) || []
      )
    },
    configOptions() {
      const options = []
      this.configuration.Categories?.forEach((cat) => {
        cat.Features.forEach((opt) => {
          options.push({
            shortId: opt.ShortId,
            name: opt.Name,
          })
        })
      })
      return options
    },
    matchedStockCar() {
      return this.allCars.length > 0 ? this.allCars[0] : null
    },
  },
  watch: {
    configurationCode() {
      this.clearBestMatchedStockCars()
      this.fetchStock()
    },
  },
  mounted() {
    this.fetchStock()
  },
  methods: {
    ...mapMutations({
      setBestMatchingStockCars: 'stockcars/setBestMatchingStockCars',
      setShouldCreateMatchingStockQuote: 'quote/setShouldCreateMatchingStockQuote',
      setPrevQuote: 'quote/setPrevQuote',
      setCreateMatchingStockQuoteOptions: 'quote/setCreateMatchingStockQuoteOptions',
    }),
    clearBestMatchedStockCars() {
      this.setBestMatchingStockCars({})
    },
    async fetchStock() {
      if (!this.configurationCode) return

      const options = []
      for (const [optId, checked] of Object.entries(this.filterOptions)) {
        if (checked) options.push(optId)
      }
      try {
        const matchingStock = await StockMatching.getMatchingStockCars(this.configurationCode, {
          engine: this.filterDrive,
          transmission: this.filterDrive,
          interior: this.filterInterior,
          color: this.filterColor,
          options,
        })
        this.setBestMatchingStockCars(matchingStock)
        this.isLoading = false
      } catch (e) {
        console.log(e)
      }
    },
    getMatchingItems() {
      return []
    },
    getMatchOptions(car) {
      const matchingConfigOptions = this.configOptions
        .map((option) => (car.StockOptions?.split(' ').includes(option.shortId) ? option : null))
        .filter((option) => !!option)
      const extraOptions = []
      const unionList = this.sortMatchOptions([car])

      // Use union list to display all options for each car beside each other
      unionList.forEach((option, index) => {
        const matchOption = car.MatchOptions.find((x) => x.ShortId === option)
        extraOptions.push(
          matchOption
            ? {
                shortId: matchOption.ShortId,
                name: matchOption.Name,
                type: matchOption.Type,
              }
            : { shortId: `missing-extra${index}`, name: ' ' }
        )
      })
      return matchingConfigOptions.concat(extraOptions)
    },
    showMatchingStockCarToNewQuotePrompt(car) {
      this.selectedMatchedStockCar = car
      this.showMatchingStockCarPrompt = true
    },
    closeMatchingStockCarToNewQuotePrompt() {
      this.selectedMatchedStockCar = null
      this.showMatchingStockCarPrompt = false
    },
    applyToNewQuote(options) {
      let car = this.selectedMatchedStockCar
      this.setPrevQuote(this.quote)
      this.setShouldCreateMatchingStockQuote(true)
      this.setCreateMatchingStockQuoteOptions(options)
      this.clearBestMatchedStockCars()
      this.$router.push({
        name: 'new-stockcar-komnumber-page',
        params: {
          komnummer: car.CommissionNumber,
          cstType: this.$route.params.cstType,
        },
      })
    },
    sortMatchOptions(optionsList) {
      // Get only additional options first
      let additionalOptionsList = optionsList?.map((x) =>
        x.MatchOptions.filter(
          (y) =>
            y.ShortId?.length &&
            y.TypeOption === 'OptionsMore' &&
            (y.Type === stockMatchOptionType.additional ||
              y.Type === stockMatchOptionType.aditional)
        ).map((z) => z.ShortId)
      )
      if (!optionsList) {
        return optionsList
      }

      return sortBy(union(...additionalOptionsList))
    },
    async fetchStockCarByCommissionNumber(commissionNumber) {
      if (this.dealerSettings.newStockFlowEnabled) {
        const results = await StockCarsNewApi.getStockcarsNew({
          search: commissionNumber,
          size: 1,
          page: 1,
          includeNationWide: true,
        })
        return results?.[0] ?? null
      }
      return await stockCarsService.getStockCarByKomnumber(commissionNumber)
    },
    async setStockCarDetails(commissionNumber) {
      this.stockCarDetails = commissionNumber
        ? await this.fetchStockCarByCommissionNumber(commissionNumber)
        : null
    },
    async setReservationStatusId(commissionNumber) {
      this.stockCarForReservationStatus = commissionNumber
        ? await this.fetchStockCarByCommissionNumber(commissionNumber)
        : null
    },
  },
}
</script>

<style lang="scss" scoped>
.stock-match-banner {
  position: fixed;
  top: 85px;
  left: calc(50% + 720px - 1rem);
  height: 800px;
  max-width: 500px;
  z-index: 1;
}

//min width of banner is 300px, main content takes 1440px
//as we occupy one half of empty spaces min width to show banner is 2040
@media all and (min-width: 2040px) {
  .stock-match-banner {
    visibility: visible;
  }
}

@media all and (max-width: 2040px) {
  .stock-match-banner {
    visibility: hidden;
  }
}

:deep(.single-car-container) {
  button {
    margin-left: 0 !important;
    width: 100%;
  }
  dl > * {
    padding-left: 0 !important;
  }
  .car-container > *:not(dl) {
    margin-left: 0 !important;
  }
  .car-container {
    padding: 20px;
  }
}
.theme-skoda {
  .single-car-container {
    border-color: var(--color-primary-lighter);
  }
  .stock-match-title {
    background-color: var(--color-primary-lighter);
    color: var(--color-primary);
  }
}
.stock-match-title {
  text-align: center;
  line-height: 1.5;
  background-color: $color-primary;
  color: $white;
  padding-top: 0.5rem;
  padding-bottom: 0.5rem;
}
.single-car-container {
  border: 5px solid;
  border-top: 0;
  border-color: $color-primary;

  --column-width: 240px;
  line-height: $line-height-base;

  :deep(dl) {
    display: flex;
    flex-direction: column;
    padding: 0.5rem 0 2rem;
  }
  :deep(dd),
  :deep(dt) {
    min-height: $line-height-base * 1em;
  }
  :deep(dd) {
    order: 2;
    margin-bottom: 1rem;

    & span {
      transition: all 150ms;
    }

    &.option:hover {
      overflow: visible;
      z-index: 99;

      & span {
        background: $gray-lightest;
        position: relative;
        box-shadow: $shadow-md;
        padding: 0 0.25rem;
      }
    }
  }
  :deep(dt),
  :deep(.price) {
    font-weight: $font-weight-bold;
  }
  :deep(.image-wrapper) {
    order: 0;
    text-align: center;
    margin-bottom: 0px;
  }
  :deep(.config-type) {
    order: 0;
    margin-bottom: 0px;
    text-align: center;
  }
  :deep(.full-match-container) {
    order: 0;
    display: inline-block;
    font-size: 0.75rem;
    line-height: 1rem;
    font-weight: $font-weight-bold;
    color: $color-success;
    text-align: center;
    line-height: 1rem;
    margin-bottom: 0px;

    > *:not(.full-match) {
      display: none;
    }
  }
  :deep(.car-image) {
    width: 100%;
    display: block;
    border: 1px solid $gray-light;
  }
  :deep(.title-wrapper) {
    order: 1;
  }
  :deep(.spec-group) {
    margin-bottom: 0;
  }
}
</style>
